import React, { useState, useEffect, useRef } from "react";
import queryString from "query-string";
import { Form, Progress, Alert } from "reactstrap";
import { CSSTransition } from "react-transition-group";

import { useLocalization } from "../../containers/I18nProvider";
import { WithCaptcha } from '../../containers/captcha';
import logo from './img/logo.png';

const EnvelopeIcon = () => (
  <svg width="18" height="18" fill="none" viewBox="0 0 24 24"
    stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0
         002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const IdIcon = () => (
  <svg width="18" height="18" fill="none" viewBox="0 0 24 24"
    stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
  </svg>
);

const LockIcon = () => (
  <svg width="18" height="18" fill="none" viewBox="0 0 24 24"
    stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0
         00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8
         0v4h8z" />
  </svg>
);

const EyeOpenIcon = () => (
  <svg width="18" height="18" fill="none" viewBox="0 0 24 24"
    stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943
         9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const EyeClosedIcon = () => (
  <svg width="18" height="18" fill="none" viewBox="0 0 24 24"
    stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97
         9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242
         4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0
         0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0
         01-4.132 5.411m0 0L21 21" />
  </svg>
);

const LoginForm = ({ api, location, TurnstileComponent, ...props }) => {
  const nodeRef = useRef(null);
  const { translate, changeDefaultLocale } = useLocalization();

  const [onLogin, setOnLogin] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);
  const [error, setError] = useState({ open: false, message: "" });
  const [identifierNumber, setIdentifierNumber] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const parameters = queryString.parse(location.search);

  useEffect(() => {
    changeDefaultLocale('es');
    setShowForm(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setOnLogin(true);
    setError({ open: false, message: "" });

    const encodedPassword = encodeURIComponent(password);
    api.userPassLogin({ username, password: encodedPassword, serviceId: identifierNumber }, parameters)
      .then((response) => {
        setOnLogin(false);
        window.location = parameters.redirect_uri + "?" + queryString.stringify(response);
      })
      .catch((error) => {
        const _translate = translate(error.errorCode);
        let message = translate("RequestLoginErrorMessage");
        if (_translate) {
          message = _translate;
        } else if (error.details) {
          message = error.details;
        }
        setOnLogin(false);
        setError({ open: true, message });
      });
  };

  return (
    <CSSTransition
      in={showForm}
      timeout={300}
      classNames="formshow"
      nodeRef={nodeRef}
      unmountOnExit
    >
      <div className="coltel-wrapper" ref={nodeRef}>
        <div className="coltel-card">

          <div className="coltel-logo-wrap">
            <img src={logo} alt="Coltel" className="coltel-logo" />
          </div>

          <h1 className="coltel-title">{translate("ClientAuthentication")}</h1>

          <Form onSubmit={onSubmit}>

            {/* Email field */}
            <div className="coltel-field">
              <span className="coltel-field-icon coltel-field-icon--left">
                <EnvelopeIcon />
              </span>
              <input
                id="coltel-username"
                className="coltel-input"
                type="text"
                required
                disabled={onLogin}
                autoCorrect="off"
                autoCapitalize="none"
                autoComplete="username"
                placeholder={translate("PlaceholderUserName")}
                onChange={e => setUsername(e.target.value)}
              />
            </div>

            {/* Password field */}
            <div className="coltel-field">
              <span className="coltel-field-icon coltel-field-icon--left">
                <LockIcon />
              </span>
              <input
                id="coltel-password"
                className="coltel-input"
                type={showPassword ? "text" : "password"}
                required
                disabled={onLogin}
                autoComplete="current-password"
                placeholder={translate("PlaceholderPassword")}
                onChange={e => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="coltel-field-icon coltel-field-icon--right coltel-eye-btn"
                aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                onClick={() => setShowPassword(v => !v)}
              >
                {showPassword ? <EyeClosedIcon /> : <EyeOpenIcon />}
              </button>
            </div>

            {/* Identifier number field */}
            <div className="coltel-field">
              <span className="coltel-field-icon coltel-field-icon--left">
                <IdIcon />
              </span>
              <input
                id="coltel-identifier"
                className="coltel-input"
                type="text"
                required
                disabled={onLogin}
                autoCorrect="off"
                autoCapitalize="none"
                autoComplete="off"
                placeholder={translate("PlaceholderIdentifierNumber")}
                onChange={e => setIdentifierNumber(e.target.value)}
              />
            </div>

            {/* Remember device */}
            <label className="coltel-remember-label">
              <input
                type="checkbox"
                className="coltel-remember-checkbox"
                checked={rememberDevice}
                onChange={e => setRememberDevice(e.target.checked)}
              />
              <span>{translate("RememberDevice")}</span>
            </label>

            {/* Turnstile */}
            <div className="coltel-turnstile">
              <TurnstileComponent setTurnstileSuccess={props.setTurnstileSuccess} />
            </div>

            {/* Submit */}
            {!onLogin ? (
              <button
                type="submit"
                className="coltel-btn-submit"
                disabled={!props.turnstileSuccess}
              >
                {translate("ButtonEnter")}
              </button>
            ) : (
              <Progress animated color="primary" value={100} className="coltel-progress" />
            )}

          </Form>

          <Alert
            color="danger"
            isOpen={error.open}
            toggle={() => setError({ open: false })}
            className="coltel-error-alert"
          >
            {error.message}
          </Alert>

        </div>
      </div>
    </CSSTransition>
  );
};

export default WithCaptcha(LoginForm, { useInsideForm: true });
